import { useState, useEffect, useContext, useCallback, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { Alert, Autocomplete, Box, FormControl, FormGroup, InputLabel, MenuItem, Select, Snackbar, Stack, TextField, Accordion, AccordionSummary, AccordionDetails } from "@mui/material";
import { treeActions } from "../../redux/slices/treeSlice";
import { ParentScreenCtx } from "/src/pages/workbench_build/WorkbenchBuild";
import { api } from "../../redux/api";
import { cutomDbEncodingConstant, encodingConstant } from "../../constants/constantList";
import CommonGrid from "../Repository/CommonGrid";
import LightTooltip from "../../commonComponents/LightTooltip";
import { cloneDeep, debounce } from "lodash";
import { getUniqueWorkflowItemIdFromArray, removeLowerCaseAndUndefined } from "../../utils/utilFunctions";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import WorkflowConnectionPanel from "../ItemProperties/WorkflowProperties/Common/WorkflowConnectionPanel";
import { SectionHeader } from "../ItemProperties/Forms/SectionHeader";
import { adapterTypeAdditionalProperties } from "../ItemProperties/WorkflowProperties/SendData/SendDataProperties";

function ErrorHandlingProperties() {
  const [errorHandlerName, setErrorHandlerName] = useState("");
  const [templateGroup, setTemplateGroup] = useState("");
  const [outboundTemplate, setOutboundTemplate] = useState("");
  const [workflowList, setWorkflowList] = useState([]);
  const selectedRow = useRef({});
  const [selectedRowState, setSelectedRowState] = useState({});
  const [encoding, setEncoding] = useState("");
  const [templateGroupOptions, setTemplateGroupOptions] = useState([]);
  const [outboundTemplateOptions, setOutboundTemplateOptions] = useState([]);
  const { account } = useSelector(state => state.queryParams)
  const [rowData, setRowData] = useState([]);
  const [gridApi, setGridApi] = useState('');
  const parentScreen = useContext(ParentScreenCtx);
  const [showAlert, setShowAlert] = useState({ mssg: '', type: 'error', isOpen: false });
  const [connectionInfo, setConnectionInfo] = useState({});

  const { leftTree, rightTree } = useSelector(state => state.treeData[parentScreen]);
  const wfProperties = rightTree?.tree?.templateHeader;
  const translatedData = rightTree?.tree?.body?.translatedData;
  const translatedDataRef = useRef(translatedData);
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const encodingOptions = Object.keys(cutomDbEncodingConstant).map((e, i) => {
    return (
      <MenuItem value={cutomDbEncodingConstant[e]} key={i}>
        {t(cutomDbEncodingConstant[e])}
      </MenuItem>
    );
  });

  const deleteActionHandler = (e) => {
    const data = [];
    e.api.forEachNode((node) => {
      if (e.data.id !== node.data.id) data.push(node.data);
    });
    columnHandler([...data], 0, e.api);
    selectedRow.current = { ...data[0] };
    setErrorHandlerName(data[0]?.OUTBOUND_NAME || "");
    const tempGrp = data[0]?.TEMPLATE_LOCATION?.split("/")[1] === "STANDARD_ERROR_TEMPLATES" ? "Standard Error Templates" : data[0]?.TEMPLATE_LOCATION?.split("/")[1];
    setTemplateGroup(tempGrp || templateGroupOptions[0]);
    setOutboundTemplate(data[0]?.TEMPLATE?.replace(/.json$/, "") || outboundTemplateOptions[0] || t("SELECT"));
    setEncoding(cutomDbEncodingConstant[Object.keys(cutomDbEncodingConstant).find((key) => cutomDbEncodingConstant[key] === data[0]?.ENCODING)] || "UTF-8");
  };

  const CustomCellRenderer = (info) => {
    const { value } = info;
    return (
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          height: "2rem",
        }}
      >
        <div className='truncated-text' title={value}>
          {value}
        </div>
        <div className='flex align-center'>
          <LightTooltip onClick={() => deleteActionHandler(info)}>
            <span
              style={{ cursor: "pointer" }}
              className='DELETE_MUI'
            ></span>
          </LightTooltip>
        </div>
      </div>
    );
  };

  const customClickHandlerOnAdd = (e) => {

    const newColId = getUniqueWorkflowItemIdFromArray(translatedDataRef.current);

    const newColumn = {
      ADAPTER_TYPE: "FILE",
      APPEND_FILE: "FALSE",
      ENCODING: "UTF-8",
      ID: newColId,
      OUTBOUND_NAME: `UNNAMED${newColId}`,
      TEMPLATE: "errortemplate_en.json",
      TEMPLATE_LOCATION: "STANDARD_TEMPLATE_LIBRARY/STANDARD_ERROR_TEMPLATES/outbound_templates",
      key: 1,
    }

    const data = [];
    const selectedRows = e.api.getSelectedRows();
    e.api.forEachNode((node) => {
      data.push(node.data);
    });
    data.splice(+(selectedRows?.[0]?.id || 0) + 1, 0, {
      ...newColumn,
    });
    selectedRow.current = { ...newColumn };
    setErrorHandlerName(newColumn?.OUTBOUND_NAME || "");
    const tempGrp = newColumn?.TEMPLATE_LOCATION?.split("/")[1] === "STANDARD_ERROR_TEMPLATES" ? "Standard Error Templates" : newColumn?.TEMPLATE_LOCATION?.split("/")[1];
    setTemplateGroup(tempGrp || templateGroupOptions[0]);
    setOutboundTemplate(newColumn?.TEMPLATE?.replace(/.json$/, "") || outboundTemplateOptions[0] || t("SELECT"));
    setEncoding(cutomDbEncodingConstant[Object.keys(cutomDbEncodingConstant).find((key) => cutomDbEncodingConstant[key] === newColumn?.ENCODING)] || "UTF-8");
    columnHandler(
      [...data],
      data.length && selectedRows?.length ? +selectedRows?.[0]?.id + 1 : 0,
      e.api
    );
  }

  const columnHandler = useCallback((data = [], selectionIndex, gridAPI, fromSubmitHandler, avoidUpdatingSelectedRow) => {
    if (!gridAPI) {
      setRowData([]);
      return;
    }

    const rowData = data?.map((e, i) => {
      return { ...e, key: e.OUTBOUND_NAME, id: i };
    });

    gridAPI.setGridOption('rowData', rowData);

    const updatedRowData = data?.map(e => removeLowerCaseAndUndefined(e));

    !fromSubmitHandler &&
      dispatch(treeActions.setWorkflowProperties({
        ...wfProperties,
        WORKFLOW: {
          ...wfProperties.WORKFLOW,
          "ERROR_HANDLER_LIST": updatedRowData
        }
      }));

      const updatedTranslatedData = cloneDeep(translatedDataRef.current);
      updatedTranslatedData[0]["ERROR_HANDLER_LIST"] = updatedRowData || [];

      !fromSubmitHandler && dispatch(treeActions.setNewRightTreeTranslatedData({
        screen: parentScreen,
        type: rightTree.currentTab,
        data: updatedTranslatedData,
      }));

    setTimeout(() => {
      gridAPI.forEachNode((node) => {
        if (node?.data?.id === selectionIndex) {
          node.setSelected(true);
          if (!avoidUpdatingSelectedRow) {
            selectedRow.current = node.data;
          }
          setSelectedRowState(node.data);
          gridAPI.ensureNodeVisible(node);
        }
      });

      if (!rowData?.length) {
        selectedRow.current = {};
        setSelectedRowState({});
      };
    }, 100);
  }, [wfProperties, dispatch, setRowData, setSelectedRowState]);

  const gridOptions = {
    columnDefs: [
      {
        addNewControl: true,
        addControlLabel: t("MNU_NEW_ERR_HANDLER"),
        headerName: t("LBL_ERROR_HANDLER_AVAILABLE"),
        field: "key",
        customClickHandlerOnAdd: customClickHandlerOnAdd,
        cellRenderer: (info) => CustomCellRenderer(info),
        editable: false,
        resizable: false,
      },
    ],
    suppressMovableColumns: true,
  };

  const handleGridUpdate = useCallback((key, value, bulkValues, deleteValues, avoidUpdatingSelectedRow) => {
    let updatedNodes = [];
    gridApi && gridApi.forEachNode((node) => {
      let tempNode = cloneDeep(node.data);
      if (node?.data?.id === selectedRow.current?.id) {
        if (key) {
          tempNode[key] = value;
        } else {
          bulkValues && Object.keys(bulkValues).forEach((e) => {
            if ((bulkValues[e] === undefined || bulkValues[e] === null) && tempNode?.hasOwnProperty(e)) delete tempNode[e];
            else tempNode[e] = bulkValues[e];
          });
        }

        deleteValues && deleteValues.forEach((e) => {
          tempNode[e] && delete tempNode[e];
        });
      }
      updatedNodes.push(tempNode);
    })
    columnHandler(updatedNodes, selectedRow.current?.id, gridApi, true, avoidUpdatingSelectedRow);
  }, [gridApi, selectedRow, columnHandler]);

  const submitHandler = useCallback((key, value, bulkValues, deleteValues, avoidUpdatingSelectedRow) => {
    let selectedErrorHandler = { ...wfProperties.WORKFLOW?.ERROR_HANDLER_LIST?.[selectedRow.current?.id] };
    if(!selectedErrorHandler) return;

    if (Object.keys(selectedErrorHandler).length) {
      if (key) {
        if (key === "OUTBOUND_NAME"
          && (wfProperties.WORKFLOW?.ERROR_HANDLER_LIST
            ?.filter((item) => item.ID !== selectedRow.current?.ID)
            ?.map(e => e?.OUTBOUND_NAME)?.includes(value) || value.trim() === "")) {
          setErrorHandlerName(selectedErrorHandler.OUTBOUND_NAME);
          setShowAlert({ mssg: t("BOX_ERRHANDLER_NAMES_MUST_BE_UNIQUE"), isOpen: true, type: 'error' });
          return;
        }


        if (value === undefined || value === null || value === "") {
          selectedErrorHandler?.hasOwnProperty(key) && delete selectedErrorHandler[key];
          selectedRow.current?.hasOwnProperty(key) && delete selectedRow.current[key];
        } else {
          selectedErrorHandler[key] = value;
          selectedRow.current[key] = value;
        }
      }
      else {
        bulkValues && Object.keys(bulkValues).forEach((e) => {
          if ((bulkValues[e] === undefined || bulkValues[e] === null || bulkValues[e] === "")) {
            selectedErrorHandler?.hasOwnProperty(e) && delete selectedErrorHandler[e];
            selectedRow.current?.hasOwnProperty(e) && delete selectedRow.current[e];
          }
          else {
            selectedErrorHandler[e] = bulkValues[e];
            selectedRow.current[e] = bulkValues[e];
          }
        });
        // selectedErrorHandler = { ...selectedErrorHandler, ...bulkValues };
        // selectedRow.current = { ...selectedRow.current, ...bulkValues };
      }

      if (deleteValues) {
        deleteValues.forEach((e) => {
          selectedErrorHandler[e] && delete selectedErrorHandler[e];
          selectedRow.current[e] && delete selectedRow.current[e];
        })
      }

      setSelectedRowState({ ...selectedRow.current });
      gridApi && handleGridUpdate(key, value, bulkValues, deleteValues, avoidUpdatingSelectedRow);
    }

    // if (key === "OUTBOUND_NAME") {
    //   dispatch(treeActions.setWorkflowProperties({
    //   }));
    // }
    setTimeout(() => {
      dispatch(treeActions.setWorkflowProperties({
        ...wfProperties,
        WORKFLOW: {
          ...wfProperties.WORKFLOW,
          "ERROR_HANDLER_LIST": wfProperties.WORKFLOW?.ERROR_HANDLER_LIST?.map((e, i) => {
            if (i === selectedRow.current?.id)
              return removeLowerCaseAndUndefined(selectedErrorHandler);
            return removeLowerCaseAndUndefined(e);
          })
        }
      }));
    });

  }, [wfProperties, selectedRow, gridApi, dispatch, t, setErrorHandlerName, setShowAlert, setSelectedRowState, handleGridUpdate]);

  const handleRow = (options) => {
    const selectedRows = options.api.getSelectedRows();
    if (selectedRows?.length === 0) return;
    if (selectedRow?.current?.ID === selectedRows[0]?.ID) return;
    selectedRow.current = { ...selectedRows[0] };
    setSelectedRowState({ ...selectedRows[0] });
    setErrorHandlerName(selectedRows[0]?.OUTBOUND_NAME || "");
    const tempGrp = selectedRows[0]?.TEMPLATE_LOCATION?.split("/")[1] === "STANDARD_ERROR_TEMPLATES" ? "Standard Error Templates" : selectedRows[0]?.TEMPLATE_LOCATION?.split("/")[1];
    setTemplateGroup(tempGrp || templateGroupOptions[0]);
    setOutboundTemplate(selectedRows[0]?.TEMPLATE?.replace(/.json$/, "") || outboundTemplateOptions[0] || t("SELECT"));
    setEncoding(cutomDbEncodingConstant[Object.keys(cutomDbEncodingConstant).find((key) => cutomDbEncodingConstant[key] === selectedRows[0]?.ENCODING)] || "UTF-8");
    options.api.redrawRows();
  }

  const debounceChangeHandler = useCallback(debounce(submitHandler, 500), [
    rightTree.currentTab,
    rightTree.tree,
    gridApi,
  ]);

  const debounceNameChangeHandler = useCallback(debounce(submitHandler, 200), [
    rightTree.currentTab,
    rightTree.tree,
    gridApi,
  ]);

  useEffect(() => {
    // Only fetch template data if gridApi is available and we don't have workflowList yet
    if (gridApi && workflowList.length === 0) {
      dispatch(api?.endpoints?.getOutboundTemplateList.initiate(
        { account: account },
      ))
        .then((res) => {
          let data = res?.data?.data?.workflow_groups;
          setTemplateGroupOptions([t("SELECT"), "Standard Error Templates", ...data.map((template) => template?.name)] || []);
          let wfList = [
            ...data, {
              name: "Standard Error Templates",
              outbound_templates: [
                {
                  name: "errortemplate_en",
                  id: "Standard Error Template",
                  location: "server",
                  type: "TEMPLATE"
                },
                {
                  name: "errortemplate_jp",
                  id: "Standard Error Template",
                  location: "server",
                  type: "TEMPLATE"
                },
                {
                  name: "errortemplate_verbose_en",
                  id: "Standard Error Template",
                  location: "server",
                  type: "TEMPLATE"
                }
              ]
            }
          ];
          setWorkflowList(wfList);
        });
    }
  }, [gridApi, account, dispatch, t]);

  // Separate useEffect for initializing grid data - only run when ERROR_HANDLER_LIST changes from external sources
  useEffect(() => {
    if (gridApi && wfProperties?.WORKFLOW?.ERROR_HANDLER_LIST) {
      const rowData = wfProperties?.WORKFLOW?.ERROR_HANDLER_LIST?.map((errorHandler, i) => {
        return {
          ...errorHandler,
          key: errorHandler?.OUTBOUND_NAME,
          id: i
        };
      });

      gridApi.setGridOption('rowData', rowData);

      setTimeout(() => {
        gridApi?.forEachNode((node) => {
          const selectedId = selectedRow.current?.id || 0;
          if (node?.data?.id === selectedId)
            node.setSelected(true);
        })

        if (!rowData?.length) {
          selectedRow.current = {};
          setSelectedRowState({});
        };
      }, 200);
    }
  }, [gridApi, wfProperties?.WORKFLOW?.ERROR_HANDLER_LIST]);

  useEffect(() => {
    if (workflowList?.length > 0) {
      const newObTempOptions = workflowList.filter(
        (template) => template.name === templateGroup
      )[0]?.outbound_templates?.map((template) => template.name) || [];

      setOutboundTemplateOptions([t("SELECT"), ...newObTempOptions]);
    }
    // eslint-disable-next-line
  }, [templateGroup]);

  const handleConnectionDetailsChange = useCallback((value) => {

    if (value !== null) {
      // Check if 'remove' exists in the value
      if (value.remove) {
        submitHandler(null, null, null, value.remove, true);
        return; // Exit early if 'remove' is handled
      }
      // Check if 'add' exists in the value
      if (value.add) {
        submitHandler(null, null, value.add, null, true);
        return;
      }
      // To handle updates
      const entries = Object.entries(value);
      const [key, val] = entries[entries.length - 1] || [];
      submitHandler(key, val, null, null, true);
    }
  }, [submitHandler]);



  const debouncedConnectionDetailsChange = useCallback(debounce(handleConnectionDetailsChange, 0), [
    handleConnectionDetailsChange,
  ]);

  useEffect(() => {
    if (selectedRowState) {
      setConnectionInfo({ ...selectedRowState?.OUTBOUND });
    }
  }, [selectedRowState?.key, selectedRowState?.OUTBOUND?.OVERRIDE]);

  useEffect(() => {
    translatedDataRef.current = translatedData;
    return () => {
      translatedDataRef.current = null;
    }
  }, [translatedData]);

  return (
    <FormGroup className="height-100 width-100 padding-0 overflow-auto no-flex-wrap">
      <Box className="" sx={{ width: 'calc(100% -  32px) !important', minWidth: 275, padding: '8px 16px' }}>
        {leftTree.tree.body.selectedNode?.type === "workflow" ? (
          <Stack spacing={'16px'} className="tp-segment">
            {/* <p style={styles.heading}>ID: {1}</p> */}
            <div className="width-100" style={{ height: "200px", marginBottom: "20px" }}>
              <CommonGrid
                gridOptions={gridOptions}
                rowData={rowData}
                getGridAPI={(e, gridApi) => {
                  setGridApi(gridApi.api)
                }}
                containerStyle={{ height: "100% !important", marginTop: "8px" }}
                gridResizable={true}
                gridProps={{
                  rowSelection: 'single',
                  onSelectionChanged: handleRow,
                }}
              />
            </div>

            <FormControl variant='outlined' className='width-100' size='small'>
              <TextField
                autoComplete={"off"}
                size='small'
                value={errorHandlerName || ""}
                label={t("NAME")}
                // onBlur={(e) => {
                //   const val = e.target.value;
                // }}
                onChange={(e) => {
                  const { value } = e.target;
                  setErrorHandlerName(value);
                  debounceNameChangeHandler('OUTBOUND_NAME', value);
                }}
                disabled={Object.keys(selectedRow.current)?.length === 0}
              />
            </FormControl>

            <p style={styles.heading}>{t("LBL_SELECT_TEMPLATE")}</p>

            <FormControl variant="outlined" className="width-100" size="small">
              <InputLabel>{t("SELECT_TEMPLATE_GROUP")}</InputLabel>
              <Select
                className={"width-100"}
                MenuProps={{
                  PaperProps: {
                    sx: {
                      maxHeight: 300,
                    },
                  },
                }}
                value={templateGroup}
                onChange={(e) => {
                  setTemplateGroup(e.target.value);
                  const tempGrpName = (e.target.value === t("STANDARD_ERROR_TEMPLATES")) ?
                    "STANDARD_ERROR_TEMPLATES" :
                    e.target.value;

                  const acc = e.target.value === t("STANDARD_ERROR_TEMPLATES") ? "STANDARD_TEMPLATE_LIBRARY" : account;

                  if (tempGrpName === t("SELECT"))
                    debounceChangeHandler(null, null, null, ["TEMPLATE", "TEMPLATE_LOCATION"]);
                  else
                    debounceChangeHandler("TEMPLATE_LOCATION", `${acc}/${tempGrpName}/outbound_templates`,
                      null, ["TEMPLATE"]);
                }}
                disabled={Object.keys(selectedRow.current)?.length === 0}
              >
                {templateGroupOptions.map((item, index) => {
                  return <MenuItem value={item} key={index}>
                    {item}
                  </MenuItem>
                })}
              </Select>
            </FormControl>

            <FormControl variant="outlined" className="width-100" size="small">
              <InputLabel>{t("LBL_SELECT_OUTBOUND_TEMPLATE")}</InputLabel>
              <Select
                className={"width-100"}
                MenuProps={{
                  PaperProps: {
                    sx: {
                      maxHeight: 300,
                    },
                  },
                }}
                value={outboundTemplate}
                onChange={(e) => {
                  setOutboundTemplate(e.target.value);
                  if (e.target.value === t("SELECT"))
                    debounceChangeHandler(null, null, null, ["TEMPLATE"]);
                  else
                    debounceChangeHandler("TEMPLATE", `${e.target.value}.json`);
                }}
                disabled={Object.keys(selectedRow.current)?.length === 0}
              >
                {outboundTemplateOptions.map((item, index) => {
                  return <MenuItem value={item} key={index}>
                    {item}
                  </MenuItem>
                })}
              </Select>
            </FormControl>

            <FormControl variant="outlined" className="width-100" size="small">
              <InputLabel>{t("ENCODING")}</InputLabel>
              <Select
                className={"width-100"}
                MenuProps={{
                  PaperProps: {
                    sx: {
                      maxHeight: 300,
                    },
                  },
                }}
                value={encoding}
                onChange={(e) => {
                  setEncoding(e.target.value);
                  debounceChangeHandler('ENCODING', e.target.value);
                }}
                disabled={Object.keys(selectedRow.current)?.length === 0}
              >
                {encodingOptions}
              </Select>
            </FormControl>

            {/* Connection details */}
            {<Accordion sx={styles.accordianContainer} defaultExpanded>
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                aria-controls="connection-info"
                id="connection-info"
                className="accordian-override"
                sx={styles.accordianHeader}
              >
                <SectionHeader text='Connection Details' />
              </AccordionSummary>
              <AccordionDetails sx={styles.accordianContainer}>
                <WorkflowConnectionPanel key={selectedRow.current?.ID} source='ERROR_HANDLER' screen='SEND_DATA' connectionDetails={selectedRow.current} handleConnectionDetailsChange={handleConnectionDetailsChange} />
              </AccordionDetails>
            </Accordion>}
          </Stack>
        ) : (
          <div className='panel-no-data'>{t('LBL_NO_WORKFLOW_ITEM_SELECTED')}</div>
        )}
        <Snackbar
          open={showAlert.isOpen}
          onClose={() => setShowAlert({ ...showAlert, isOpen: false })}
          autoHideDuration={4000}>
          <Alert
            onClose={() => setShowAlert({ ...showAlert, isOpen: false })}
            severity={showAlert.type}
            variant="filled"
            sx={{ width: "100%" }}>
            {showAlert.mssg}
          </Alert>
        </Snackbar>
      </Box>
    </FormGroup>
  )
}

const styles = {

  heading: {
    fontSize: "12px",
  },

  connectionCheckbox: {
    display: "flex"
  },

  selectPdc: {
    display: "flex",
    justifyContent: "space-between",
    flexDirection: "row",
    gap: "10px"
  },

  accordianContainer: {
    border: "none",
    padding: "0",
    margin: "0",
    boxShadow: "none",
    backgroundColor: "unset",
  },
  accordianHeader: {
    border: "none",
    padding: "0",
    margin: "0",
    backgroundColor: "unset",
  },
  accordianContent: {
    border: "none",
    padding: "0 15px",
    margin: "0",
    backgroundColor: "unset",
  }
}

const adapterTypeHandlers = {
  BridgeGateSocket: (node) => ({ SYNCHRONOUS: node?.SYNCHRONOUS }),
  FILE: (node) => ({ RUN_ON_SERVER: node?.RUN_ON_SERVER }),
  SOCKET: (node) => ({ SYNCHRONOUS: node?.SYNCHRONOUS })
};

export default ErrorHandlingProperties;
